import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Zap, Crown, Star, Users, Gift, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Monthly price IDs
const MONTHLY_PRICE_IDS = {
  basic:    process.env.REACT_APP_STRIPE_BASIC_PRICE_ID,
  standard: process.env.REACT_APP_STRIPE_STANDARD_PRICE_ID,
  premium:  process.env.REACT_APP_STRIPE_PREMIUM_PRICE_ID,
  family:   process.env.REACT_APP_STRIPE_FAMILY_PRICE_ID,
};

// Annual price IDs (set these in Vercel env vars)
const ANNUAL_PRICE_IDS = {
  basic:    process.env.REACT_APP_STRIPE_BASIC_ANNUAL_PRICE_ID    || process.env.REACT_APP_STRIPE_BASIC_PRICE_ID,
  standard: process.env.REACT_APP_STRIPE_STANDARD_ANNUAL_PRICE_ID || process.env.REACT_APP_STRIPE_STANDARD_PRICE_ID,
  premium:  process.env.REACT_APP_STRIPE_PREMIUM_ANNUAL_PRICE_ID  || process.env.REACT_APP_STRIPE_PREMIUM_PRICE_ID,
  family:   process.env.REACT_APP_STRIPE_FAMILY_ANNUAL_PRICE_ID   || process.env.REACT_APP_STRIPE_FAMILY_PRICE_ID,
};

const MONTHLY_PRICES = { basic: 3.99, standard: 5.99, premium: 9.99, family: 14.99 };
const ANNUAL_PRICES  = { basic: 2.99, standard: 4.79, premium: 7.99, family: 11.99 };

const TIER_ICONS = { basic: Zap, standard: Star, premium: Crown, family: Users };

const TIERS = [
  {
    id: 'basic', name: 'Basic',
    features: ['Connect up to 4 streaming services', 'Sports scores & standings', 'Drop alerts for new episodes', 'Chrome extension', 'Up to 3 folders'],
    highlight: false,
  },
  {
    id: 'standard', name: 'Standard',
    features: ['Connect up to 6 streaming services', 'MoodQ personalized recommendations', 'Spoiler Shield', 'Watch DNA insights', 'QMates (up to 3 friends)'],
    highlight: true,
  },
  {
    id: 'premium', name: 'Premium',
    features: ['Unlimited streaming services', 'Auto-detect viewing history', 'Unlimited QMates', 'Early access to new features'],
    highlight: false,
  },
];

const FAMILY_PLAN = {
  id: 'family', name: 'Family',
  features: [
    'Everything in Premium',
    'Up to 5 family accounts',
    'Each member gets own watchlist',
    'Shared family queue',
    'Invite members by email',
  ],
};

// ── Gift Subscription Modal ───────────────────────────────────────────────────

function GiftModal({ onClose, userId, getToken }) {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [selectedTier, setSelectedTier] = useState('standard');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGift = async () => {
    if (!recipientEmail.trim() || !/\S+@\S+\.\S+/.test(recipientEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    const priceId = billingCycle === 'annual'
      ? ANNUAL_PRICE_IDS[selectedTier]
      : MONTHLY_PRICE_IDS[selectedTier];
    if (!priceId) {
      toast.error('Price not configured — contact support');
      return;
    }
    setLoading(true);
    try {
      const token = await getToken();
      const res = await axios.post(`${API}/stripe/gift-subscription`, {
        recipient_email: recipientEmail,
        price_id: priceId,
        message,
      }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start gift checkout');
      setLoading(false);
    }
  };

  const price = billingCycle === 'annual'
    ? ANNUAL_PRICES[selectedTier]
    : MONTHLY_PRICES[selectedTier];
  const period = billingCycle === 'annual' ? '/yr' : '/mo';

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[#111] border border-white/10 rounded-2xl p-6 w-full max-w-md space-y-5"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-violet-400" />
            <h2 className="text-lg font-black text-white" style={{ fontFamily: 'Outfit' }}>Gift Qude</h2>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-neutral-400">
          Give someone a Qude subscription. They'll receive an email with instructions to activate it.
        </p>

        <div>
          <label className="text-xs text-neutral-500 block mb-1.5">Recipient's email</label>
          <Input placeholder="friend@example.com" value={recipientEmail}
            onChange={e => setRecipientEmail(e.target.value)}
            className="bg-black/30 border-white/10 text-white placeholder:text-neutral-600 rounded-xl h-11" />
        </div>

        <div>
          <label className="text-xs text-neutral-500 block mb-2">Plan</label>
          <div className="grid grid-cols-3 gap-2">
            {['basic', 'standard', 'premium'].map(t => (
              <button key={t} onClick={() => setSelectedTier(t)}
                className={`py-2.5 rounded-xl text-sm font-semibold border transition-all capitalize ${
                  selectedTier === t
                    ? 'border-violet-500/60 bg-violet-500/20 text-white'
                    : 'border-white/10 text-neutral-400 hover:text-white'
                }`}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs text-neutral-500 block mb-2">Billing</label>
          <div className="flex gap-2">
            {['monthly', 'annual'].map(b => (
              <button key={b} onClick={() => setBillingCycle(b)}
                className={`flex-1 py-2 rounded-xl text-sm font-semibold border transition-all ${
                  billingCycle === b
                    ? 'border-violet-500/60 bg-violet-500/20 text-white'
                    : 'border-white/10 text-neutral-400 hover:text-white'
                }`}>
                {b === 'annual' ? 'Annual (save 20%)' : 'Monthly'}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs text-neutral-500 block mb-1.5">Personal message (optional)</label>
          <Input placeholder="Enjoy your streaming!" value={message}
            onChange={e => setMessage(e.target.value)}
            className="bg-black/30 border-white/10 text-white placeholder:text-neutral-600 rounded-xl h-11" />
        </div>

        <Button onClick={handleGift} disabled={loading}
          className="w-full bg-gradient-to-r from-violet-600 to-[#4A9EFF] text-white rounded-full font-bold text-base py-3 hover:opacity-90 disabled:opacity-50 gap-2">
          {loading
            ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : <><Gift className="w-4 h-4" /> Gift {selectedTier} for ${price}{period}</>
          }
        </Button>
      </div>
    </div>
  );
}

// ── Main Pricing Page ─────────────────────────────────────────────────────────

export default function Pricing() {
  const { user, getToken } = useAuth();
  const [currentTier, setCurrentTier] = useState('free');
  const [loading, setLoading] = useState('');
  const [billing, setBilling] = useState('monthly');
  const [showGift, setShowGift] = useState(false);

  useEffect(() => {
    const fetchSub = async () => {
      if (!user) return;
      try {
        const token = await getToken();
        const res = await axios.get(`${API}/stripe/subscription-status/${user.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrentTier(res.data.subscription_tier);
      } catch (err) {}
    };
    fetchSub();
  }, [user, getToken]);

  const selectTier = async (tierId) => {
    const priceId = billing === 'annual'
      ? ANNUAL_PRICE_IDS[tierId]
      : MONTHLY_PRICE_IDS[tierId];

    if (!priceId) {
      toast.error('Price not configured — contact support');
      return;
    }
    setLoading(tierId);
    try {
      const token = await getToken();
      const res = await axios.post(`${API}/stripe/create-checkout-session`,
        { user_id: user.id, price_id: priceId, billing_cycle: billing },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (err) {
      toast.error('Failed to start checkout. Please try again.');
      setLoading('');
    }
  };

  const downgradeToFree = async () => {
    setLoading('free');
    try {
      const token = await getToken();
      await axios.post(`${API}/subscription/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentTier('free');
      toast.success('Downgraded to Free plan.');
    } catch (err) {
      toast.error('Failed to downgrade.');
    } finally {
      setLoading('');
    }
  };

  const savings = (id) => {
    const monthly = MONTHLY_PRICES[id];
    const annual  = ANNUAL_PRICES[id];
    return Math.round((1 - annual / monthly) * 100);
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-24" data-testid="pricing-page">

      {/* Header */}
      <div className="text-center space-y-3">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight" style={{ fontFamily: 'Outfit' }}>
          Choose Your Plan
        </h1>
        <p className="text-neutral-500">
          Currently on: <span className="text-violet-400 font-semibold capitalize">{currentTier}</span> plan
        </p>

        {/* Monthly / Annual toggle */}
        <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/5 border border-white/10">
          {['monthly', 'annual'].map(b => (
            <button key={b} onClick={() => setBilling(b)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                billing === b ? 'bg-violet-600 text-white' : 'text-neutral-400 hover:text-white'
              }`}>
              {b === 'annual' ? (
                <span className="flex items-center gap-2">
                  Annual
                  <span className="text-xs px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-bold">
                    Save 20%
                  </span>
                </span>
              ) : 'Monthly'}
            </button>
          ))}
        </div>
      </div>

      {/* Individual plans */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {TIERS.map(tier => {
          const Icon = TIER_ICONS[tier.id];
          const isCurrent = currentTier === tier.id;
          const price = billing === 'annual' ? ANNUAL_PRICES[tier.id] : MONTHLY_PRICES[tier.id];

          return (
            <div key={tier.id}
              className={`relative p-8 rounded-2xl border transition-all ${
                tier.highlight
                  ? 'bg-[#0F0F0F] border-violet-500/40 shadow-[0_0_30px_-10px_rgba(124,58,237,0.3)]'
                  : 'bg-[#0A0A0A] border-white/5 hover:border-white/15'
              }`}
              data-testid={`pricing-card-${tier.id}`}>

              {tier.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-violet-600 to-[#4A9EFF] text-xs font-bold text-white">
                  Most Popular
                </div>
              )}

              <div className="flex items-center gap-2 mb-4">
                <Icon className="w-5 h-5 text-violet-400" />
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit' }}>{tier.name}</h3>
              </div>

              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-black text-white" style={{ fontFamily: 'JetBrains Mono' }}>
                  ${price}
                </span>
                <span className="text-sm text-neutral-500">/{billing === 'annual' ? 'mo' : 'mo'}</span>
              </div>

              {billing === 'annual' && (
                <div className="flex items-center gap-2 mb-5">
                  <span className="text-xs text-neutral-600 line-through">${MONTHLY_PRICES[tier.id]}/mo</span>
                  <span className="text-xs text-emerald-400 font-semibold">Save {savings(tier.id)}%</span>
                </div>
              )}
              {billing === 'monthly' && <div className="mb-5" />}

              <ul className="space-y-3 mb-8">
                {tier.features.map(f => (
                  <li key={f} className="flex items-start gap-3 text-sm text-neutral-300">
                    <Check className="w-4 h-4 text-violet-400 mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              <Button onClick={() => selectTier(tier.id)}
                disabled={isCurrent || !!loading}
                className={`w-full rounded-full font-semibold ${
                  isCurrent
                    ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 cursor-default'
                    : tier.highlight
                    ? 'bg-gradient-to-r from-violet-600 to-[#4A9EFF] text-white hover:opacity-90'
                    : 'bg-white/5 border border-white/10 text-white hover:bg-white/10'
                }`}
                data-testid={`select-tier-${tier.id}`}>
                {loading === tier.id
                  ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  : isCurrent ? 'Current Plan'
                  : `Get ${tier.name}`
                }
              </Button>
            </div>
          );
        })}
      </div>

      {/* Family Plan */}
      <div className="relative p-8 rounded-2xl border border-[#4A9EFF]/30 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #051a20 0%, #0a0a0f 100%)' }}>
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-5"
          style={{ background: 'radial-gradient(circle, #4A9EFF, transparent)', transform: 'translate(30%, -30%)' }} />
        <div className="flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-[#4A9EFF]" />
              <h3 className="text-xl font-black text-white" style={{ fontFamily: 'Outfit' }}>Family Plan</h3>
              <span className="text-xs px-2.5 py-0.5 rounded-full bg-[#4A9EFF]/20 text-[#4A9EFF] font-bold border border-[#4A9EFF]/30">
                Up to 5 accounts
              </span>
            </div>
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-3xl font-black text-white" style={{ fontFamily: 'JetBrains Mono' }}>
                ${billing === 'annual' ? ANNUAL_PRICES.family : MONTHLY_PRICES.family}
              </span>
              <span className="text-sm text-neutral-500">/mo</span>
              {billing === 'annual' && (
                <span className="text-xs text-emerald-400 font-semibold ml-1">Save {savings('family')}%</span>
              )}
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {FAMILY_PLAN.features.map(f => (
                <li key={f} className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-[#4A9EFF] flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="flex-shrink-0">
            <Button onClick={() => selectTier('family')}
              disabled={currentTier === 'family' || !!loading}
              className="px-8 py-3 rounded-full font-bold text-white hover:opacity-90 disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #0ea5e9, #4A9EFF)' }}>
              {loading === 'family'
                ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : currentTier === 'family' ? 'Current Plan'
                : 'Get Family Plan'
              }
            </Button>
          </div>
        </div>
      </div>

      {/* Gift + Downgrade row */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
        <button onClick={() => setShowGift(true)}
          className="flex items-center gap-2 px-6 py-3 rounded-full border border-violet-500/30 text-violet-400 hover:bg-violet-500/10 transition-all text-sm font-semibold">
          <Gift className="w-4 h-4" /> Gift a Subscription
        </button>

        <p className="text-sm text-neutral-500 text-center">
          Free plan includes 2 services and basic features.
          {currentTier !== 'free' && (
            <button onClick={downgradeToFree} disabled={loading === 'free'}
              className="ml-2 text-neutral-400 hover:text-white underline underline-offset-2 transition-colors"
              data-testid="downgrade-free-btn">
              Downgrade to Free
            </button>
          )}
        </p>
      </div>

      {/* Gift modal */}
      {showGift && (
        <GiftModal
          onClose={() => setShowGift(false)}
          userId={user?.id}
          getToken={getToken}
        />
      )}
    </div>
  );
}
